import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  PlusIcon,
  EditIcon,
  SaveIcon,
  Loader2Icon,
  UserCheckIcon,
} from 'lucide-react'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileSearchField,
  MobileSheet,
  mobile,
} from '../components/mobile'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import {
  createCrew,
  createEmployee,
  listCrews,
  listEmployees,
  updateCrew,
  updateEmployee,
  type CrewInput,
  type CrewRow,
  type EmployeeInput,
  type EmployeeRow,
} from '../api/team'
import {
  adminFetch,
  formatAdminApiError,
  type LoginAccessFormState,
  type ManagedUser,
} from '../lib/adminApi'
import {
  emptyLoginAccessForm,
  loginFormFromUser,
  LoginAccessPanel,
} from '../components/team/LoginAccessPanel'
import { USER_ROLES, normalizeRole, isAdminRole } from '../lib/permissions'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'

type TopTab = 'people' | 'crews'
type EditorTab = 'profile' | 'login'

type PersonKind = 'employee' | 'login_only'

interface PersonRow {
  key: string
  kind: PersonKind
  employee: EmployeeRow | null
  user: ManagedUser | null
  displayName: string
  email: string
  roleLabel: string | null
  isActive: boolean
  invitePending: boolean
}

const emptyEmployeeForm = (): EmployeeInput => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  employee_number: '',
  employment_type: 'full_time',
  is_available_for_scheduling: true,
  is_active: true,
  user_profile_id: null,
})

const emptyCrewForm = (): CrewInput => ({
  name: '',
  description: '',
  lead_employee_id: null,
  is_active: true,
  is_subcontractor: false,
  member_ids: [],
})

export function TeamManagement() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { session } = useAuth()
  const { can, canManageUser, isAdmin, profile } = usePermissions()

  const canViewTeam = can('team.view')
  const canEditTeam = can('team.manage')
  const canViewUsers = can('users.view')
  const canManageUsers = can('users.manage')
  const canInviteUsers = can('users.invite') && can('users.manage')
  const canSeePage = canViewTeam || canViewUsers

  const initialTab = searchParams.get('tab') === 'crews' ? 'crews' : 'people'
  const [tab, setTab] = useState<TopTab>(initialTab)
  const [employees, setEmployees] = useState<EmployeeRow[]>([])
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [usersError, setUsersError] = useState<string | null>(null)
  const [crews, setCrews] = useState<CrewRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [personModal, setPersonModal] = useState(false)
  const [editorTab, setEditorTab] = useState<EditorTab>('profile')
  const [editingEmployee, setEditingEmployee] = useState<EmployeeRow | null>(null)
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null)
  const [createEmployeeRecord, setCreateEmployeeRecord] = useState(true)
  const [empForm, setEmpForm] = useState(emptyEmployeeForm())
  const [loginForm, setLoginForm] = useState<LoginAccessFormState>(emptyLoginAccessForm())
  /** none | existing | new — for login tab when person has no user yet */
  const [loginSetup, setLoginSetup] = useState<'none' | 'existing' | 'new'>('none')

  const [crewModal, setCrewModal] = useState(false)
  const [editingCrew, setEditingCrew] = useState<CrewRow | null>(null)
  const [crewForm, setCrewForm] = useState(emptyCrewForm())

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const tasks: Promise<void>[] = []

      if (canViewTeam || canEditTeam) {
        tasks.push(
          (async () => {
            const [emps, crs] = await Promise.all([listEmployees(), listCrews()])
            setEmployees(emps)
            setCrews(crs)
          })()
        )
      } else {
        // users.view only — still need employees to hide linked ones from orphan list
        tasks.push(
          (async () => {
            try {
              setEmployees(await listEmployees())
            } catch {
              setEmployees([])
            }
            setCrews([])
          })()
        )
      }

      if (canViewUsers && session) {
        tasks.push(
          (async () => {
            try {
              setUsersError(null)
              const usersUrl = profile?.tenant_id
                ? '/api/admin/users?tenant_id=' + encodeURIComponent(profile.tenant_id)
                : '/api/admin/users'
              const data = await adminFetch(usersUrl, session)
              setUsers(data.users || [])
            } catch (e: any) {
              setUsers([])
              setUsersError(formatAdminApiError(e.message || 'Failed to load logins'))
            }
          })()
        )
      } else {
        setUsers([])
        setUsersError(null)
      }

      await Promise.all(tasks)
    } catch (e: any) {
      setError(e.message || 'Failed to load team')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canSeePage) load()
  }, [session?.access_token])

  useEffect(() => {
    const t = searchParams.get('tab')
    if (t === 'crews' || t === 'people') setTab(t)
  }, [searchParams])

  const usersById = useMemo(() => {
    const m = new Map<string, ManagedUser>()
    for (const u of users) m.set(u.id, u)
    return m
  }, [users])

  const linkedProfileIds = useMemo(() => {
    const s = new Set<string>()
    for (const e of employees) {
      if (e.user_profile_id) s.add(e.user_profile_id)
    }
    return s
  }, [employees])

  const people: PersonRow[] = useMemo(() => {
    const rows: PersonRow[] = []

    for (const e of employees) {
      const u = e.user_profile_id ? usersById.get(e.user_profile_id) || null : null
      const role = u ? normalizeRole(u.role) : null
      rows.push({
        key: `emp-${e.id}`,
        kind: 'employee',
        employee: e,
        user: u,
        displayName: `${e.first_name} ${e.last_name}`.trim() || e.email || 'Employee',
        email: e.email || u?.email || e.linked_user?.email || '',
        roleLabel: role
          ? USER_ROLES.find((r) => r.value === role)?.label || role
          : null,
        isActive: e.is_active !== false && (u ? u.is_active !== false : true),
        invitePending: !!u?.invite_pending,
      })
    }

    if (canViewUsers) {
      for (const u of users) {
        if (linkedProfileIds.has(u.id)) continue
        const role = normalizeRole(u.role)
        rows.push({
          key: `user-${u.id}`,
          kind: 'login_only',
          employee: null,
          user: u,
          displayName:
            `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          email: u.email,
          roleLabel: USER_ROLES.find((r) => r.value === role)?.label || role,
          isActive: u.is_active !== false,
          invitePending: !!u.invite_pending,
        })
      }
    }

    return rows
  }, [employees, users, usersById, linkedProfileIds, canViewUsers])

  const filteredPeople = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return people
    return people.filter((p) =>
      `${p.displayName} ${p.email} ${p.roleLabel || ''}`.toLowerCase().includes(q)
    )
  }, [people, search])

  const filteredCrews = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return crews
    return crews.filter((c) => `${c.name} ${c.description || ''}`.toLowerCase().includes(q))
  }, [crews, search])

  const setTopTab = (key: TopTab) => {
    setTab(key)
    setSearch('')
    setSearchParams(key === 'crews' ? { tab: 'crews' } : { tab: 'people' }, { replace: true })
  }

  const openCreatePerson = () => {
    setEditingEmployee(null)
    setEditingUser(null)
    setCreateEmployeeRecord(true)
    setEmpForm(emptyEmployeeForm())
    setLoginForm(emptyLoginAccessForm())
    // Default to create/invite login when permitted; otherwise profile-only.
    setLoginSetup(canInviteUsers ? 'new' : 'none')
    setEditorTab('profile')
    setPersonModal(true)
  }

  const openPerson = async (row: PersonRow) => {
    if (row.kind === 'employee' && row.employee) {
      const e = row.employee
      setEditingEmployee(e)
      setEditingUser(row.user)
      setCreateEmployeeRecord(true)
      setEmpForm({
        first_name: e.first_name || '',
        last_name: e.last_name || '',
        email: e.email || '',
        phone: e.phone || '',
        employee_number: e.employee_number || '',
        employment_type: e.employment_type || 'full_time',
        is_available_for_scheduling: e.is_available_for_scheduling !== false,
        is_active: e.is_active !== false,
        user_profile_id: e.user_profile_id || null,
      })
      if (row.user) {
        setLoginForm(loginFormFromUser(row.user))
        setLoginSetup('existing')
      } else if (e.user_profile_id && canViewUsers && session) {
        setLoginForm(
          emptyLoginAccessForm({
            email: e.email || '',
            first_name: e.first_name || '',
            last_name: e.last_name || '',
            phone: e.phone || '',
          })
        )
        setLoginSetup('existing')
        setEditorTab('profile')
        setPersonModal(true)
        try {
          const data = await adminFetch(`/api/admin/users/${e.user_profile_id}`, session)
          if (data.user) {
            setEditingUser(data.user)
            setLoginForm(loginFormFromUser(data.user))
            setLoginSetup('existing')
          }
        } catch (err: any) {
          setError(formatAdminApiError(err.message || 'Failed to load login'))
          setLoginSetup('none')
        }
        return
      } else {
        setLoginForm(
          emptyLoginAccessForm({
            email: e.email || '',
            first_name: e.first_name || '',
            last_name: e.last_name || '',
            phone: e.phone || '',
          })
        )
        setLoginSetup('none')
      }
      setEditorTab('profile')
    } else if (row.user) {
      if (!canManageUser(row.user.role) && !canViewUsers) return
      setEditingEmployee(null)
      setEditingUser(row.user)
      setCreateEmployeeRecord(false)
      setEmpForm({
        ...emptyEmployeeForm(),
        first_name: row.user.first_name || '',
        last_name: row.user.last_name || '',
        email: row.user.email || '',
        phone: row.user.phone || '',
      })
      setLoginForm(loginFormFromUser(row.user))
      setLoginSetup('existing')
      setEditorTab(canViewUsers ? 'login' : 'profile')
    }
    setPersonModal(true)
  }

  const savePerson = async () => {
    try {
      setSaving(true)
      setError(null)

      const wantsProfile =
        createEmployeeRecord || !!editingEmployee || editorTab === 'profile'
      if (wantsProfile && createEmployeeRecord && !editingEmployee) {
        if (!empForm.first_name.trim() || !empForm.last_name.trim()) {
          throw new Error('First and last name are required for an employee profile')
        }
      }
      if (editingEmployee && canEditTeam) {
        if (!empForm.first_name.trim() || !empForm.last_name.trim()) {
          throw new Error('First and last name are required')
        }
      }

      let userProfileId: string | null =
        editingEmployee?.user_profile_id || editingUser?.id || null

      // Login setup
      if (canViewUsers && (loginSetup === 'new' || editingUser)) {
        if (loginSetup === 'new' && !editingUser) {
          const wantsInvite = loginForm.createMode === 'invite'
          if (wantsInvite && !canInviteUsers) {
            throw new Error('You do not have permission to invite logins (users.invite)')
          }
          if (!wantsInvite && !canManageUsers) {
            throw new Error('You do not have permission to create logins (users.manage)')
          }
          if (!loginForm.email.trim()) throw new Error('Email is required for a login')
          if (loginForm.createMode === 'create' && (loginForm.password || '').length < 8) {
            throw new Error('Password must be at least 8 characters')
          }
          const created = await adminFetch('/api/admin/users', session, {
            method: 'POST',
            body: JSON.stringify({
              email: loginForm.email.trim(),
              first_name: (empForm.first_name || loginForm.first_name).trim(),
              last_name: (empForm.last_name || loginForm.last_name).trim(),
              phone: empForm.phone || loginForm.phone || null,
              role: loginForm.role,
              is_active: loginForm.is_active,
              customer_id: loginForm.role === 'customer' ? loginForm.customer_id || null : null,
              permissions: loginForm.permissions,
              send_invite: loginForm.createMode === 'invite',
              ...(loginForm.createMode === 'create'
                ? { password: loginForm.password, send_invite: false }
                : {}),
            }),
          })
          userProfileId = created.user?.id || null
          if (!userProfileId) throw new Error('User created but no id returned')
        } else if (editingUser) {
          if (!canManageUsers || !canManageUser(editingUser.role)) {
            if (loginForm.password || loginForm.email.trim() !== (editingUser.email || '')) {
              throw new Error('You do not have permission to change this login')
            }
          } else {
            if (!loginForm.email.trim()) throw new Error('Email is required for a login')
            if (loginForm.password && loginForm.password.length < 8) {
              throw new Error('Password must be at least 8 characters')
            }

            const updated = await adminFetch(`/api/admin/users/${editingUser.id}`, session, {
              method: 'PATCH',
              body: JSON.stringify({
                email: loginForm.email.trim(),
                first_name: empForm.first_name || loginForm.first_name,
                last_name: empForm.last_name || loginForm.last_name,
                phone: empForm.phone || loginForm.phone || null,
                role: loginForm.role,
                is_active: loginForm.is_active,
                customer_id: loginForm.role === 'customer' ? loginForm.customer_id || null : null,
                permissions: loginForm.permissions,
                ...(loginForm.password ? { password: loginForm.password } : {}),
              }),
            })
            if (loginForm.password && !updated.password_updated) {
              throw new Error('Password was not updated. Restart the API proxy and try again.')
            }
            userProfileId = editingUser.id
          }
        }
      }

      if (canEditTeam) {
        if (editingEmployee) {
          await updateEmployee(editingEmployee.id, {
            first_name: empForm.first_name,
            last_name: empForm.last_name,
            email: empForm.email || loginForm.email || null,
            phone: empForm.phone,
            employee_number: empForm.employee_number,
            employment_type: empForm.employment_type,
            is_available_for_scheduling: empForm.is_available_for_scheduling,
            is_active: empForm.is_active,
            user_profile_id: loginSetup === 'none' ? null : userProfileId,
          })
        } else if (createEmployeeRecord) {
          await createEmployee({
            first_name: empForm.first_name,
            last_name: empForm.last_name,
            email: empForm.email || loginForm.email || null,
            phone: empForm.phone,
            employee_number: empForm.employee_number,
            employment_type: empForm.employment_type,
            is_available_for_scheduling: empForm.is_available_for_scheduling,
            is_active: empForm.is_active,
            user_profile_id: userProfileId,
          })
        }
      }

      setPersonModal(false)
      await load()
    } catch (e: any) {
      setError(formatAdminApiError(e.message || 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  const handleResendInvite = async () => {
    if (!editingUser) return
    try {
      await adminFetch(`/api/admin/users/${editingUser.id}/resend-invite`, session, {
        method: 'POST',
      })
      alert(`Invite resent to ${editingUser.email}`)
    } catch (e: any) {
      setError(formatAdminApiError(e.message))
    }
  }

  const handleDeactivateLogin = async () => {
    if (!editingUser || !canManageUsers || !canManageUser(editingUser.role)) return
    if (editingUser.id === profile?.id) {
      setError('You cannot deactivate your own login')
      return
    }
    try {
      setSaving(true)
      setError(null)
      const updated = await adminFetch(`/api/admin/users/${editingUser.id}`, session, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: false }),
      })
      const nextUser = updated.user as ManagedUser
      setEditingUser(nextUser)
      setLoginForm(loginFormFromUser(nextUser))
      await load()
    } catch (e: any) {
      setError(formatAdminApiError(e.message || 'Failed to deactivate'))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteLogin = async () => {
    if (!editingUser || !canManageUsers || !canManageUser(editingUser.role)) return
    if (editingUser.id === profile?.id) {
      setError('You cannot delete your own login')
      return
    }
    const ok = window.confirm(
      `Permanently delete login for ${editingUser.email}? This cannot be undone.`
    )
    if (!ok) return
    try {
      setSaving(true)
      setError(null)
      await adminFetch(`/api/admin/users/${editingUser.id}`, session, {
        method: 'DELETE',
      })
      setPersonModal(false)
      setEditingUser(null)
      setEditingEmployee(null)
      await load()
    } catch (e: any) {
      setError(formatAdminApiError(e.message || 'Failed to delete login'))
    } finally {
      setSaving(false)
    }
  }

  const unlinkLogin = async () => {
    if (!editingEmployee || !canEditTeam) return
    try {
      setSaving(true)
      await updateEmployee(editingEmployee.id, { user_profile_id: null })
      setLoginSetup('none')
      setEditingUser(null)
      setEditingEmployee({ ...editingEmployee, user_profile_id: null, linked_user: null })
      await load()
    } catch (e: any) {
      setError(e.message || 'Failed to unlink login')
    } finally {
      setSaving(false)
    }
  }

  const openCreateCrew = () => {
    setEditingCrew(null)
    setCrewForm(emptyCrewForm())
    setCrewModal(true)
  }

  const openEditCrew = (c: CrewRow) => {
    setEditingCrew(c)
    setCrewForm({
      name: c.name || '',
      description: c.description || '',
      lead_employee_id: c.lead_employee_id || null,
      is_active: c.is_active !== false,
      is_subcontractor: c.is_subcontractor === true,
      member_ids: c.member_ids || [],
    })
    setCrewModal(true)
  }

  const saveCrew = async () => {
    if (!crewForm.name.trim()) {
      setError('Crew name is required')
      return
    }
    try {
      setSaving(true)
      setError(null)
      if (editingCrew) await updateCrew(editingCrew.id, crewForm)
      else await createCrew(crewForm)
      setCrewModal(false)
      await load()
    } catch (e: any) {
      setError(e.message || 'Failed to save crew')
    } finally {
      setSaving(false)
    }
  }

  const toggleMember = (employeeId: string) => {
    setCrewForm((f) => {
      const set = new Set(f.member_ids || [])
      if (set.has(employeeId)) set.delete(employeeId)
      else set.add(employeeId)
      return { ...f, member_ids: Array.from(set) }
    })
  }

  const activeEmployees = employees.filter((e) => e.is_active !== false)

  if (!canSeePage) {
    return (
      <MobilePage>
        <MobileEmptyState title="Access denied" description="You do not have permission to view Team." />
      </MobilePage>
    )
  }

  const canOpenPerson = (row: PersonRow) => {
    if (row.kind === 'employee' && canEditTeam) return true
    if (row.kind === 'employee' && canViewUsers) return true
    if (row.user && canViewUsers) return true
    return false
  }

  const loginReadOnly =
    !!editingUser &&
    ((isAdminRole(editingUser.role) && !isAdmin) || !canManageUser(editingUser.role) || !canManageUsers)

  return (
    <MobilePage>
      <MobilePageHeader
        title="Team"
        subtitle={
          <span className="hidden sm:inline">
            People, logins, permissions, and crews for job assignment.
          </span>
        }
        actions={
          (canEditTeam || canInviteUsers) ? (
            <button
              type="button"
              onClick={tab === 'people' ? openCreatePerson : openCreateCrew}
              className={mobile.primaryBtn}
            >
              <PlusIcon className="w-5 h-5" />
              <span className="sm:hidden">Add</span>
              <span className="hidden sm:inline">
                {tab === 'people' ? 'Add person' : 'Add crew'}
              </span>
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">{error}</div>
      )}
      {usersError && canViewUsers && (
        <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm">
          {usersError}
        </div>
      )}

      <div className={`${mobile.surface} overflow-hidden`}>
        <div className="flex gap-1 border-b border-gray-100 overflow-x-auto scrollbar-none">
          <button
            type="button"
            onClick={() => setTopTab('people')}
            className={`px-4 min-h-[48px] text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
              tab === 'people'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500'
            }`}
          >
            People ({people.length})
          </button>
          {canViewTeam && (
            <button
              type="button"
              onClick={() => setTopTab('crews')}
              className={`px-4 min-h-[48px] text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${
                tab === 'crews'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500'
              }`}
            >
              Crews ({crews.length})
            </button>
          )}
        </div>
        <div className="p-3">
          <MobileSearchField
            value={search}
            onChange={setSearch}
            placeholder={tab === 'people' ? 'Search people…' : 'Search crews…'}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 py-14">
          <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" /> Loading…
        </div>
      ) : tab === 'people' ? (
        <>
          <div className="native-mobile-list space-y-2">
            {filteredPeople.length === 0 ? (
              <MobileEmptyState
                title="No people yet"
                description="Add field staff or invite a login."
              />
            ) : (
              filteredPeople.map((p) => (
                <MobileListRow
                  key={p.key}
                  onClick={canOpenPerson(p) ? () => openPerson(p) : undefined}
                  chevron={canOpenPerson(p)}
                  title={p.displayName}
                  subtitle={
                    p.kind === 'login_only'
                      ? 'Login only'
                      : p.employee?.employee_number
                        ? `#${p.employee.employee_number}`
                        : undefined
                  }
                  badges={
                    <>
                      {p.roleLabel && (
                        <span className={`${mobile.badge} bg-blue-50 text-blue-700`}>
                          {p.roleLabel}
                        </span>
                      )}
                      {p.invitePending ? (
                        <span className={`${mobile.badge} bg-amber-50 text-amber-700`}>
                          Invite pending
                        </span>
                      ) : (
                        <span
                          className={`${mobile.badge} ${
                            p.isActive
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {p.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </>
                  }
                  meta={
                    p.email || p.user || p.employee?.linked_user ? (
                      <div className="flex items-center gap-1 truncate">
                        {(p.user || p.employee?.linked_user) && (
                          <UserCheckIcon className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                        )}
                        <span className="truncate">
                          {p.email || p.employee?.linked_user?.email || '—'}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">No login</span>
                    )
                  }
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Email / Login</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium w-20" />
                </tr>
              </thead>
              <tbody>
                {filteredPeople.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No people yet. Add field staff or invite a login.
                    </td>
                  </tr>
                ) : (
                  filteredPeople.map((p) => (
                    <tr key={p.key} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-medium text-gray-900">
                        {p.displayName}
                        {p.kind === 'login_only' && (
                          <span className="ml-2 text-xs text-gray-400 font-normal">Login only</span>
                        )}
                        {p.employee?.employee_number && (
                          <span className="ml-2 text-xs text-gray-400">
                            #{p.employee.employee_number}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {p.email || p.user || p.employee?.linked_user ? (
                          <span className="inline-flex items-center gap-1">
                            {(p.user || p.employee?.linked_user) && (
                              <UserCheckIcon className="w-3.5 h-3.5 text-blue-600" />
                            )}
                            {p.email || p.employee?.linked_user?.email || '—'}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No login</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {p.roleLabel ? (
                          <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">
                            {p.roleLabel}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {p.invitePending ? (
                          <span className="text-amber-600 text-xs">Invite pending</span>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                              p.isActive
                                ? 'bg-green-50 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {p.isActive ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {canOpenPerson(p) && (
                          <button
                            type="button"
                            onClick={() => openPerson(p)}
                            className="p-1.5 text-gray-500 hover:text-blue-600"
                            title="Edit"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="native-mobile-list space-y-2">
            {filteredCrews.length === 0 ? (
              <MobileEmptyState
                title="No crews yet"
                description="Create a crew to assign groups of employees to jobs."
              />
            ) : (
              filteredCrews.map((c) => (
                <MobileListRow
                  key={c.id}
                  onClick={canEditTeam ? () => openEditCrew(c) : undefined}
                  chevron={canEditTeam}
                  title={c.name}
                  subtitle={c.description || undefined}
                  badges={
                    <span
                      className={`${mobile.badge} ${
                        c.is_active !== false
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {c.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  }
                  meta={
                    <>
                      <div>
                        Lead:{' '}
                        {c.lead ? `${c.lead.first_name} ${c.lead.last_name}` : '—'}
                      </div>
                      <div>
                        {(c.member_ids || []).length} member
                        {(c.member_ids || []).length === 1 ? '' : 's'}
                        {c.is_subcontractor ? ' · Subcontractor' : ''}
                      </div>
                    </>
                  }
                />
              ))
            )}
          </div>

          <div className="native-desktop-table bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-4 py-2 font-medium">Crew</th>
                  <th className="px-4 py-2 font-medium">Lead</th>
                  <th className="px-4 py-2 font-medium">Members</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  {canEditTeam && <th className="px-4 py-2 font-medium w-20" />}
                </tr>
              </thead>
              <tbody>
                {filteredCrews.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No crews yet. Create a crew to assign groups of employees to jobs.
                    </td>
                  </tr>
                ) : (
                  filteredCrews.map((c) => (
                    <tr key={c.id} className="border-t border-gray-100">
                      <td className="px-4 py-2">
                        <p className="font-medium text-gray-900">{c.name}</p>
                        {c.description && (
                          <p className="text-xs text-gray-500">{c.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {c.lead ? `${c.lead.first_name} ${c.lead.last_name}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {(c.member_ids || []).length} member
                        {(c.member_ids || []).length === 1 ? '' : 's'}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                            c.is_active !== false
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {c.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canEditTeam && (
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => openEditCrew(c)}
                            className="p-1.5 text-gray-500 hover:text-blue-600"
                            title="Edit"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Person sheet */}
      <MobileSheet
        open={personModal}
        onClose={() => setPersonModal(false)}
        title={
          editingEmployee ? 'Edit person' : editingUser ? 'Edit login' : 'Add person'
        }
        maxWidthClass="max-w-2xl"
        footer={
          <>
            <button
              type="button"
              onClick={() => setPersonModal(false)}
              className={`${mobile.secondaryBtn} flex-1`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={savePerson}
              className={`${mobile.primaryBtn} flex-1`}
            >
              {saving ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <SaveIcon className="w-4 h-4" />
              )}
              Save
            </button>
          </>
        }
      >
            <div className="flex gap-1 border-b border-gray-100 -mx-4 px-4 mb-3">
              {(canEditTeam || editingEmployee || !editingUser) && (
                <button
                  type="button"
                  onClick={() => setEditorTab('profile')}
                  className={`px-3 min-h-[44px] text-sm font-semibold border-b-2 -mb-px ${
                    editorTab === 'profile'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  Profile
                </button>
              )}
              {canViewUsers && (
                <button
                  type="button"
                  onClick={() => setEditorTab('login')}
                  className={`px-3 min-h-[44px] text-sm font-semibold border-b-2 -mb-px ${
                    editorTab === 'login'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  Login & access
                </button>
              )}
            </div>

            <div className="space-y-3">
              {editorTab === 'profile' && (
                <>
                  {!editingEmployee && editingUser && canEditTeam && (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={createEmployeeRecord}
                        onChange={(e) => setCreateEmployeeRecord(e.target.checked)}
                      />
                      Also create field employee record
                    </label>
                  )}
                  {(!editingUser || createEmployeeRecord || editingEmployee) && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            First name *
                          </label>
                          <input
                            value={empForm.first_name}
                            disabled={!canEditTeam}
                            onChange={(e) =>
                              setEmpForm({ ...empForm, first_name: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Last name *
                          </label>
                          <input
                            value={empForm.last_name}
                            disabled={!canEditTeam}
                            onChange={(e) =>
                              setEmpForm({ ...empForm, last_name: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={empForm.email || ''}
                            disabled={!canEditTeam}
                            onChange={(e) => setEmpForm({ ...empForm, email: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Phone
                          </label>
                          <input
                            value={empForm.phone || ''}
                            disabled={!canEditTeam}
                            onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
                          />
                        </div>
                      </div>
                      {(editingEmployee || createEmployeeRecord) && canEditTeam && (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Employee #
                              </label>
                              <input
                                value={empForm.employee_number || ''}
                                onChange={(e) =>
                                  setEmpForm({ ...empForm, employee_number: e.target.value })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Employment
                              </label>
                              <select
                                value={empForm.employment_type || 'full_time'}
                                onChange={(e) =>
                                  setEmpForm({ ...empForm, employment_type: e.target.value })
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              >
                                <option value="full_time">Full time</option>
                                <option value="part_time">Part time</option>
                                <option value="contractor">Contractor</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={empForm.is_active !== false}
                                onChange={(e) =>
                                  setEmpForm({ ...empForm, is_active: e.target.checked })
                                }
                              />
                              Active
                            </label>
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={empForm.is_available_for_scheduling !== false}
                                onChange={(e) =>
                                  setEmpForm({
                                    ...empForm,
                                    is_available_for_scheduling: e.target.checked,
                                  })
                                }
                              />
                              Available for scheduling
                            </label>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {editingUser && !createEmployeeRecord && !editingEmployee && (
                    <p className="text-sm text-gray-500">
                      This is a login-only account (no field employee). Switch to Login & access to
                      edit role and permissions, or check “Also create field employee record”.
                    </p>
                  )}
                </>
              )}

              {editorTab === 'login' && (
                <>
                  {!canViewUsers ? (
                    <p className="text-sm text-gray-500">
                      You do not have permission to view logins.
                    </p>
                  ) : usersError && !editingUser && loginSetup !== 'new' ? (
                    <p className="text-sm text-amber-800">{usersError}</p>
                  ) : (
                    <>
                      {!editingUser && (
                        <div className="flex flex-col gap-1.5 text-sm mb-3">
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="loginSetup"
                              checked={loginSetup === 'none'}
                              onChange={() => setLoginSetup('none')}
                            />
                            No login
                          </label>
                          {(canInviteUsers || canManageUsers) && (
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="radio"
                                name="loginSetup"
                                checked={loginSetup === 'new'}
                                onChange={() => setLoginSetup('new')}
                              />
                              Create / invite login
                            </label>
                          )}
                        </div>
                      )}
                      {(loginSetup === 'new' || editingUser) && (
                        <LoginAccessPanel
                          value={loginForm}
                          onChange={setLoginForm}
                          editingUser={editingUser}
                          actorRole={profile?.role || 'crew_member'}
                          isNewLogin={loginSetup === 'new' && !editingUser}
                          hideNameFields={!!editingEmployee || createEmployeeRecord}
                          readOnly={loginReadOnly && !!editingUser}
                          onResendInvite={handleResendInvite}
                          canResendInvite={canInviteUsers && !!editingUser}
                          onDeactivate={
                            editingUser && canManageUsers && canManageUser(editingUser.role)
                              ? handleDeactivateLogin
                              : undefined
                          }
                          onDeleteLogin={
                            editingUser && canManageUsers && canManageUser(editingUser.role)
                              ? handleDeleteLogin
                              : undefined
                          }
                          actionBusy={saving}
                        />
                      )}
                      {editingEmployee?.user_profile_id && editingUser && canEditTeam && (
                        <button
                          type="button"
                          onClick={unlinkLogin}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Unlink login (keeps the user account)
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

      </MobileSheet>

      {/* Crew sheet */}
      <MobileSheet
        open={crewModal}
        onClose={() => setCrewModal(false)}
        title={editingCrew ? 'Edit crew' : 'Add crew'}
        footer={
          <>
            <button
              type="button"
              onClick={() => setCrewModal(false)}
              className={`${mobile.secondaryBtn} flex-1`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={saveCrew}
              className={`${mobile.primaryBtn} flex-1`}
            >
              {saving ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
              ) : (
                <SaveIcon className="w-4 h-4" />
              )}
              Save
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className={mobile.label}>Name *</label>
            <input
              value={crewForm.name}
              onChange={(e) => setCrewForm({ ...crewForm, name: e.target.value })}
              className={mobile.field}
            />
          </div>
          <div>
            <label className={mobile.label}>Description</label>
            <textarea
              value={crewForm.description || ''}
              onChange={(e) => setCrewForm({ ...crewForm, description: e.target.value })}
              rows={2}
              className={`${mobile.field} min-h-0`}
            />
          </div>
          <div>
            <label className={mobile.label}>Lead</label>
            <select
              value={crewForm.lead_employee_id || ''}
              onChange={(e) =>
                setCrewForm({ ...crewForm, lead_employee_id: e.target.value || null })
              }
              className={mobile.field}
            >
              <option value="">None</option>
              {activeEmployees.map((e) => (
                <option key={e.id} value={e.id}>
                  {formatEmployeeLabel(e)}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm min-h-[44px]">
            <input
              type="checkbox"
              checked={crewForm.is_active !== false}
              onChange={(e) => setCrewForm({ ...crewForm, is_active: e.target.checked })}
            />
            Active
          </label>
          <label className="inline-flex items-center gap-2 text-sm min-h-[44px]">
            <input
              type="checkbox"
              checked={crewForm.is_subcontractor === true}
              onChange={(e) =>
                setCrewForm({ ...crewForm, is_subcontractor: e.target.checked })
              }
            />
            Subcontractor crew
          </label>
          <div>
            <label className={mobile.label}>Members</label>
            {activeEmployees.length === 0 ? (
              <p className="text-xs text-gray-500">
                Add employees first, then assign them here.
              </p>
            ) : (
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl divide-y">
                {activeEmployees.map((e) => (
                  <label
                    key={e.id}
                    className="flex items-center gap-2 px-3 py-3 text-sm min-h-[44px] active:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={(crewForm.member_ids || []).includes(e.id)}
                      onChange={() => toggleMember(e.id)}
                    />
                    {formatEmployeeLabel(e)}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </MobileSheet>
    </MobilePage>
  )
}
